// src/pages/calendar/PublicBookingPage.tsx
// Public (unauthenticated) booking page at /book/:slug — pick a slot, leave
// contact details, answer the link's intake questions, confirm.

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { Button, Input, Textarea, Alert } from '@react/components';
import { GetBookingLinkAvailability } from '@core/api/endpoints/calendar/get_booking_link_availability';
import { BookSlot } from '@core/api/endpoints/calendar/book_slot';
import type { AvailableBookingSlot, BookingAnswer } from '@core/types/calendar';
import { dayKey, errMessage, fmtDate, fmtTime } from '@/features/calendar/time';

export function PublicBookingPage() {
  const { slug = '' } = useParams();
  const { t, locale } = useI18n();

  const [slots, setSlots] = useState<AvailableBookingSlot[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AvailableBookingSlot | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [isBooking, setIsBooking] = useState(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    setIsLoading(true);
    setError(null);
    GetBookingLinkAvailability(slug, { skipAuth: true })
      .then((res) => setSlots(res.slots ?? []))
      .catch((e) => setError(errMessage(e, t('calendar.publicBooking.loadError'))))
      .finally(() => setIsLoading(false));
  }, [slug]);

  const slotsByDay = useMemo(() => {
    const m = new Map<string, AvailableBookingSlot[]>();
    for (const s of slots ?? []) {
      if (!s.start_time) continue;
      const key = dayKey(new Date(s.start_time));
      const arr = m.get(key) ?? [];
      arr.push(s);
      m.set(key, arr);
    }
    return m;
  }, [slots]);

  const book = async () => {
    if (!selected?.start_time || !name.trim() || !email.trim()) {
      setError(t('calendar.publicBooking.validation'));
      return;
    }
    setIsBooking(true);
    setError(null);
    try {
      const answers: BookingAnswer[] = [];
      const res = await BookSlot(slug, {
        slug,
        start_time: selected.start_time,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        notes: notes.trim() || undefined,
        answers: { answers },
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }, { skipAuth: true });
      setConfirmation(res.confirmation_message || t('calendar.publicBooking.confirmed'));
    } catch (e) {
      setError(errMessage(e, t('calendar.publicBooking.bookError')));
    } finally {
      setIsBooking(false);
    }
  };

  if (confirmation) {
    return (
      <div className="mx-auto max-w-lg p-8">
        <Alert variant="success" title={t('calendar.publicBooking.confirmedTitle')}>
          {confirmation}
        </Alert>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">{t('calendar.publicBooking.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('calendar.publicBooking.subtitle')}</p>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {isLoading && (
        <div className="space-y-2" aria-busy="true">
          <div className="h-8 w-1/2 animate-pulse rounded bg-muted" />
          <div className="h-24 animate-pulse rounded bg-muted" />
        </div>
      )}

      {!isLoading && slots !== null && slots.length === 0 && (
        <p className="text-sm text-muted-foreground">{t('calendar.publicBooking.noSlots')}</p>
      )}

      {!isLoading && slotsByDay.size > 0 && (
        <div className="space-y-4">
          {Array.from(slotsByDay.entries()).map(([key, daySlots]) => (
            <div key={key}>
              <h2 className="mb-1.5 text-sm font-medium text-foreground">
                {fmtDate(daySlots[0]?.start_time, locale)}
              </h2>
              <div className="flex flex-wrap gap-1.5">
                {daySlots.map((s, i) => {
                  const isSel = selected?.start_time === s.start_time;
                  return (
                    <button
                      key={i}
                      type="button"
                      aria-pressed={isSel}
                      onClick={() => setSelected(s)}
                      className={`rounded-md border px-3 py-1.5 text-sm ${
                        isSel
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input text-foreground hover:bg-muted'
                      }`}
                    >
                      {fmtTime(s.start_time, locale)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div className="space-y-3 rounded-md border border-border p-4">
          <p className="text-sm font-medium text-foreground">
            {t('calendar.publicBooking.selectedSlot', {
              date: fmtDate(selected.start_time, locale),
              time: fmtTime(selected.start_time, locale),
            })}
          </p>
          <Input label={t('calendar.publicBooking.name')} value={name} onChange={(e) => setName(e.target.value)} required />
          <Input label={t('calendar.publicBooking.email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Input label={t('calendar.publicBooking.phone')} value={phone} onChange={(e) => setPhone(e.target.value)} />
          <Textarea label={t('calendar.publicBooking.notes')} value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          <Button onClick={book} isLoading={isBooking} fullWidth>
            {t('calendar.publicBooking.book')}
          </Button>
        </div>
      )}
    </div>
  );
}
